'use client';

import { useTranslation } from '@/context/LanguageContext';
import Link from 'next/link';
import { siteConfig } from '@/config/site';
import { useSettings } from '@/context/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faClock, faMapMarkerAlt, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { faFacebookF, faInstagram, faTwitter, faYoutube, faLinkedinIn, faWhatsapp } from '@fortawesome/free-brands-svg-icons';

export default function KontakContent() {
  const { t, language } = useTranslation();
  const { getSettingContent, getRawSettingContent } = useSettings();
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
  const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';

  const departments = [
    { name: t('contact.dept.rectorate'), phone: '(0541) 749140', email: 'rektor@unmul.ac.id', hours: 'Senin - Jumat, 08:00 - 16:00' },
    { name: t('contact.dept.academic'), phone: '(0541) 749315', email: 'akademik@unmul.ac.id', hours: 'Senin - Jumat, 08:00 - 16:00' },
    { name: t('contact.dept.student'), phone: '(0541) 749316', email: 'kemahasiswaan@unmul.ac.id', hours: 'Senin - Jumat, 08:00 - 16:00' },
    { name: t('contact.dept.finance'), phone: '(0541) 749317', email: 'keuangan@unmul.ac.id', hours: 'Senin - Jumat, 08:00 - 16:00' },
    { name: t('contact.dept.pr'), phone: '(0541) 749318', email: 'humas@unmul.ac.id', hours: 'Senin - Jumat, 08:00 - 16:00' },
    { name: t('contact.dept.admission'), phone: '(0541) 749319', email: 'pmb@unmul.ac.id', hours: 'Senin - Sabtu, 08:00 - 15:00' },
  ];

  // Dynamic Settings
  const contactAddress = getSettingContent('location_address', siteConfig.contact.address);
  const rawMapEmbed = getRawSettingContent('location_google_map_embed');
  const whatsappUrl = getSettingContent('social_whatsapp', 'https://wa.me/628115551962');

  const socials = [
    { name: 'Facebook', icon: faFacebookF, url: getSettingContent('social_facebook', siteConfig.socials.facebook), color: 'bg-blue-600' },
    { name: 'Instagram', icon: faInstagram, url: getSettingContent('social_instagram', siteConfig.socials.instagram), color: 'bg-pink-600' },
    { name: 'Twitter/X', icon: faTwitter, url: getSettingContent('social_twitter', siteConfig.socials.twitter), color: 'bg-black' },
    { name: 'YouTube', icon: faYoutube, url: getSettingContent('social_youtube', siteConfig.socials.youtube), color: 'bg-red-600' },
    { name: 'LinkedIn', icon: faLinkedinIn, url: getSettingContent('social_linkedin', siteConfig.socials.linkedin), color: 'bg-blue-700' },
  ];

  // Filter out socials without URLs (if any, though fallback usually prevents this unless fallback is empty)
  const activeSocials = socials.filter(s => s.url);


  return (
    <>
      {/* Hero */}
      <section className="pt-36 pb-20 bg-gradient-to-br from-unmulDarkGreen to-unmulGreen text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <nav className="text-sm text-green-200 mb-4 flex items-center gap-2">
              <Link href="/" className="hover:text-white">{t('nav.home')}</Link>
              <span className="mx-2">/</span>
              <span className="text-unmulYellow">{t('contact.breadcrumb')}</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">{t('contact.title')}</h1>
            <p className="text-xl text-green-100">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('contact.department.title')}</h2>

              <div className="grid sm:grid-cols-2 gap-4">
                {departments.map((dept, i) => (
                  <div key={i} className="bg-white p-5 rounded-xl border border-gray-200">
                    <h3 className="font-bold text-gray-800 mb-3">{dept.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <FontAwesomeIcon icon={faPhone} className="text-unmulGreen w-4" />
                        <span>{dept.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <FontAwesomeIcon icon={faEnvelope} className="text-unmulGreen w-4" />
                        <a href={`mailto:${dept.email}`} className="hover:text-unmulGreen">{dept.email}</a>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <FontAwesomeIcon icon={faClock} className="text-gray-400 w-4" />
                        <span>{dept.hours}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="mt-10">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('contact.location.title')}</h2>
                <div className="rounded-xl overflow-hidden shadow-lg h-96 relative bg-gray-100">
                  {rawMapEmbed ? (
                    <div
                      className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0"
                      dangerouslySetInnerHTML={{ __html: rawMapEmbed }}
                    />
                  ) : (
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.6849626549956!2d117.15399739999998!3d-0.4684587!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2df67f4bb8b9dcd1%3A0x6fd5d38233261f93!2sMulawarman%20University!5e0!3m2!1sen!2sid!4v1767950693277!5m2!1sen!2sid"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={language === 'en' ? siteNameEnglish : siteName}
                    ></iframe>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              {/* Address */}
              <div className="bg-unmulDarkGreen text-white p-6 rounded-xl mb-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-unmulYellow" />
                  {t('contact.address.title')}
                </h3>
                <p className="text-green-100 leading-relaxed">
                  {language === 'en' ? siteNameEnglish : siteName}<br />
                  {contactAddress}<br />
                  Indonesia
                </p>
              </div>

              {/* Social Media */}
              <div className="bg-white p-6 rounded-xl border border-gray-200 mb-6">
                <h3 className="font-bold text-lg text-gray-800 mb-4">{t('contact.social.title')}</h3>
                <div className="space-y-3">
                  {activeSocials.map((social, i) => (
                    <a
                      key={i}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition group"
                    >
                      <div className={`w-10 h-10 ${social.color} text-white rounded-lg flex items-center justify-center`}>
                        <FontAwesomeIcon icon={social.icon} />
                      </div>
                      <span className="font-medium text-gray-700 group-hover:text-unmulGreen">{social.name}</span>
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-gray-400 ml-auto text-xs" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Contact */}
              <div className="bg-unmulYellow p-6 rounded-xl">
                <h3 className="font-bold text-lg text-unmulDarkGreen mb-4">{t('contact.quick.title')}</h3>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 text-white py-3 px-4 rounded-lg font-bold hover:bg-green-600 transition w-full"
                >
                  <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
                  {t('contact.whatsapp')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
